import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCustomAlert } from '../../components/common/CustomAlert';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Chip,
  IconButton,
  Alert,
  TablePagination,
  TextField,
  InputAdornment,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { useTenant } from '../../contexts/TenantContext';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useOktaAuth } from '@okta/okta-react';
import { useErrorToast } from '../../components/common';

const SEARCH_DEBOUNCE_MS = 400;

const ClientUsersDialog = ({ open, client, onClose }) => {
  const api = useApi();
  const apiRef = useRef(api);
  const { refreshTenantContext, currentTenant } = useTenant();
  const { authState } = useOktaAuth();
  const { showConfirm } = useCustomAlert();
  const { showErrorToast } = useErrorToast();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(null);
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');

  const searchTimerRef = useRef(null);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const loadClientUsers = useCallback(async () => {
    if (!client?.TenantId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiRef.current.get('/users/tenant', {
        params: {
          tenantId: client.TenantId,
          page: page + 1,
          pageSize: rowsPerPage,
        },
      });

      const apiResponse = response.data;

      if (apiResponse?.success && apiResponse?.data) {
        setUsers(apiResponse.data.users || []);
        setTotalUsers(apiResponse.data.pagination?.totalItems || 0);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load users'));
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [client?.TenantId, page, rowsPerPage]);

  const loadRoles = useCallback(async () => {
    if (!client) return;
    
    try {
      const response = await apiRef.current.get('/users/roles');
      const apiResponse = response.data;
      
      let allRoles = [];
      if (apiResponse?.success && apiResponse?.data?.roles) {
        allRoles = apiResponse.data.roles;
      } else if (apiResponse?.roles) {
        allRoles = apiResponse.roles;
      }
      
      // Filter roles based on tenant type
      // JLL roles (JLL_SUPER_ADMIN, JLL_ANALYST) only for JLL tenant
      // Client roles (CLIENT_ADMIN, CLIENT_POWER_USER, CLIENT_VIEWER) only for client tenants
      const isJLLTenant = client.TenantType === 'JLL';
      
      const filteredRoles = allRoles.filter(role => {
        const roleCode = role.roleCode || role.RoleCode || '';
        const isJLLRole = roleCode.startsWith('JLL_');
        
        if (isJLLTenant) {
          // For JLL tenant, show JLL roles
          return isJLLRole;
        } else {
          // For client tenants, show only client roles (not JLL roles)
          return !isJLLRole;
        }
      });
      
      setRoles(filteredRoles);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  }, [client]);

  useEffect(() => {
    if (open && client) {
      setPage(0);
      setSearchText('');
      setSearchResults([]);
      setSelectedUser(null);
      setSelectedRole('');
      setShowNewUserForm(false);
      loadClientUsers();
      loadRoles();
    }
  }, [open, client, loadClientUsers, loadRoles]);

  useEffect(() => {
    if (open && client) {
      loadClientUsers();
    }
  }, [page, rowsPerPage]);

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await apiRef.current.get('/users', {
        params: { search: query, pageSize: 10 },
      });

      const apiResponse = response.data;
      let allUsers = [];

      if (apiResponse?.success && apiResponse?.data?.users) {
        allUsers = apiResponse.data.users;
      } else if (apiResponse?.users) {
        allUsers = apiResponse.users;
      }

      const assignedUserIds = users.map(u => u.userId);
      const availableUsers = allUsers.filter(u => !assignedUserIds.includes(u.userId));
      
      setSearchResults(availableUsers);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [users]);

  const handleSearchChange = (event, value) => {
    setSearchText(value);
    setShowNewUserForm(false);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      searchUsers(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleAddUser = async () => {
    if (!selectedRole) {
      setSnackbar({ open: true, message: 'Please select a role', severity: 'warning' });
      return;
    }

    // Check if we have a selected user or manual input
    if (!selectedUser && !searchText) {
      setSnackbar({ open: true, message: 'Please select or enter a user email', severity: 'warning' });
      return;
    }

    try {
      setAddLoading(true);
      
      // Prepare request based on whether user is selected or manually entered
      const requestData = {
        tenantId: client.TenantId,
        roleId: selectedRole,
      };
      
      if (selectedUser) {
        // User selected from search - use userId
        requestData.userId = selectedUser.userId;
      } else {
        // Manual email entry - let backend handle create or assign
        requestData.email = searchText.trim();
        requestData.fullName = newUserName.trim() || searchText.split('@')[0].replace('.', ' ');
      }
      
      // Single unified endpoint handles both scenarios
      await apiRef.current.post('/users/assign', requestData);

      setSnackbar({ open: true, message: 'User added to client successfully', severity: 'success' });
      setSelectedUser(null);
      setSelectedRole('');
      setSearchText('');
      setNewUserName('');
      setSearchResults([]);
      setShowNewUserForm(false);
      loadClientUsers();
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, 'Failed to add user'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleCreateNewUser = async () => {
    // Just call handleAddUser - it handles both scenarios
    await handleAddUser();
  };

  const handleRemoveUser = async (userId) => {
    const confirmed = await showConfirm('Remove this user from this client?', { title: 'Remove User', confirmText: 'Remove' });
    if (!confirmed) return;

    try {
      setRemoveLoading(userId);
      
      // Check if the user is removing themselves
      const currentUserEmail = authState?.idToken?.claims?.email || 
                               authState?.idToken?.claims?.preferred_username ||
                               authState?.accessToken?.claims?.email;
      
      const removedUser = users.find(u => u.userId === userId);
      const isRemovingSelf = removedUser?.email?.toLowerCase() === currentUserEmail?.toLowerCase();
      
      await apiRef.current.delete(`/users/${userId}`, {
        params: { 
          tenantOnly: true,
          tenantId: client.TenantId 
        },
      });

      setSnackbar({ open: true, message: 'User removed from client successfully', severity: 'success' });
      loadClientUsers();
      
      // If user removed themselves, refresh tenant context to update dropdown
      if (isRemovingSelf) {
        console.log('User removed themselves from tenant, refreshing context...');
        await refreshTenantContext();
        
        // If the removed tenant was the current tenant, close the dialog
        if (currentTenant?.tenantId === client.TenantId) {
          setSnackbar({ 
            open: true, 
            message: 'You have been removed from this client. Redirecting...', 
            severity: 'info' 
          });
          setTimeout(() => {
            handleClose();
          }, 1500);
        }
      }
    } catch (err) {
      showErrorToast(getApiErrorMessage(err, 'Failed to remove user'));
    } finally {
      setRemoveLoading(null);
    }
  };

  const handleClose = () => {
    setUsers([]);
    setError(null);
    setPage(0);
    setSearchText('');
    setSearchResults([]);
    setSelectedUser(null);
    setSelectedRole('');
    setShowNewUserForm(false);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6">Users - {client?.TenantName || 'Client'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {totalUsers} user{totalUsers !== 1 ? 's' : ''} assigned
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Add User to Client
          </Typography>

          {!showNewUserForm ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Autocomplete
                sx={{ flex: 1, minWidth: 200 }}
                size="small"
                options={searchResults}
                loading={searchLoading}
                value={selectedUser}
                onChange={(e, value) => setSelectedUser(value)}
                onInputChange={handleSearchChange}
                getOptionLabel={(option) => option.email || ''}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.fullName || option.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search users by name or email..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  searchText.length >= 2 ? (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="body2" color="text.secondary">No users found</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setShowNewUserForm(true);
                          setNewUserEmail(searchText.includes('@') ? searchText : '');
                        }}
                        sx={{ mt: 1 }}
                      >
                        Create New User
                      </Button>
                    </Box>
                  ) : 'Type to search...'
                }
              />

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedRole}
                  label="Role"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={addLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                onClick={handleAddUser}
                disabled={!selectedUser || !selectedRole || addLoading}
                sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
              >
                Add
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a new user and add to this client
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Email"
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <TextField
                  size="small"
                  label="Full Name (optional)"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  sx={{ flex: 1, minWidth: 150 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    label="Role"
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.roleId} value={role.roleId}>
                        {role.roleName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setShowNewUserForm(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={addLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                  onClick={handleCreateNewUser}
                  disabled={!newUserEmail || !selectedRole || addLoading}
                  sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
                >
                  Create & Add
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />


        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <Typography color="text.secondary">No users assigned to this client yet.</Typography>
            <Typography variant="body2" color="text.secondary">Use the search above to add users.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.userId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.fullName || user.displayName || 'N/A'}
                        </Typography>
                        {user.isJLLUser && (
                          <Chip label="JLL" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={user.role?.roleName || 'No Role'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveUser(user.userId)}
                          disabled={removeLoading === user.userId}
                        >
                          {removeLoading === user.userId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <PersonRemoveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={handleClose} variant="outlined">Close</Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ClientUsersDialog;
